import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function FilterDropdown({
  options = [],
  value = "",
  onChange,
  placeholder = "All",
  style,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [panelPos, setPanelPos] = useState({});
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const selectedLabel = value !== ""
    ? (options.find((o) => String(o.value) === String(value))?.label ?? "")
    : null;

  const visible = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpen = () => {
    const rect = triggerRef.current.getBoundingClientRect();
    const width = Math.max(rect.width, 200);
    const PANEL_HEIGHT = 300;
    const spaceBelow = window.innerHeight - rect.bottom;
    const pos =
      spaceBelow >= PANEL_HEIGHT || spaceBelow >= rect.top
        ? { top: rect.bottom + 2, left: rect.left, width }
        : { bottom: window.innerHeight - rect.top + 2, left: rect.left, width };
    setPanelPos(pos);
    setOpen(true);
  };

  const select = (val) => {
    onChange(val);
    setOpen(false);
    setSearch("");
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        !panelRef.current?.contains(e.target) &&
        !triggerRef.current?.contains(e.target)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <>
      <div
        ref={triggerRef}
        onClick={handleOpen}
        className="form-control d-flex align-items-center"
        style={{
          cursor: "pointer",
          height: 38,
          userSelect: "none",
          position: "relative",
          paddingRight: 28,
          ...style,
        }}
      >
        {selectedLabel ? (
          <span style={{ fontSize: "0.875rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {selectedLabel}
          </span>
        ) : (
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>
            {placeholder}
          </span>
        )}
        <span
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#adb5bd",
            fontSize: "0.75rem",
            pointerEvents: "none",
          }}
        >
          ▾
        </span>
      </div>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: "fixed",
              zIndex: 9999,
              background: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: 4,
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
              padding: 10,
              fontSize: "0.85rem",
              ...panelPos,
            }}
          >
            <input
              className="form-control form-control-sm mb-2"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            <div
              style={{
                maxHeight: 220,
                overflowY: "auto",
                borderTop: "1px solid #dee2e6",
                paddingTop: 4,
              }}
            >
              <div
                onClick={() => select("")}
                style={{
                  padding: "5px 8px",
                  cursor: "pointer",
                  borderRadius: 3,
                  fontWeight: value === "" ? 600 : 400,
                  background: value === "" ? "#e7f1ff" : "transparent",
                }}
                onMouseEnter={(e) => { if (value !== "") e.currentTarget.style.background = "#f8f9fa"; }}
                onMouseLeave={(e) => { if (value !== "") e.currentTarget.style.background = "transparent"; }}
              >
                {placeholder}
              </div>
              {visible.map((opt) => {
                const isSelected = String(opt.value) === String(value);
                return (
                  <div
                    key={String(opt.value)}
                    onClick={() => select(opt.value)}
                    style={{
                      padding: "5px 8px",
                      cursor: "pointer",
                      borderRadius: 3,
                      fontWeight: isSelected ? 600 : 400,
                      background: isSelected ? "#e7f1ff" : "transparent",
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#f8f9fa"; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
